// Free, no-API-key weather pipeline used by the WeatherWidget on the player.
//
// Step 1 — geocode a US ZIP code via api.zippopotam.us. The response is a
// fixed JSON shape; we only need the first place's lat/lng + display name.
// Step 2 — fetch the 5-day daily forecast + current temperature from
// api.open-meteo.com. Both endpoints are CORS-friendly and free, with no
// account or key required.
//
// The result is cached in-process for 30 minutes so a kiosk that loops the
// playlist every minute doesn't hammer the upstream services.

const GEO_TTL_MS = 24 * 60 * 60 * 1000; // 24h — ZIP -> lat/lng never changes
const FORECAST_TTL_MS = 30 * 60 * 1000; // 30m — refresh forecast twice per hour

export interface WeatherDay {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  highF: number;
  lowF: number;
  weatherCode: number;
}

export interface WeatherSnapshot {
  zipCode: string;
  /** "Atlanta, GA" or similar friendly label. */
  location: string;
  /** Current temperature in Fahrenheit. */
  currentF: number;
  currentWeatherCode: number;
  /** Five upcoming days, today first. */
  days: WeatherDay[];
  /** Epoch ms when this snapshot was minted. */
  fetchedAt: number;
}

interface GeoEntry {
  latitude: number;
  longitude: number;
  location: string;
  fetchedAt: number;
}

const geoCache = new Map<string, GeoEntry>();
const forecastCache = new Map<string, WeatherSnapshot>();
const inflight = new Map<string, Promise<WeatherSnapshot>>();

interface ZippopotamPlace {
  'place name'?: string;
  state?: string;
  'state abbreviation'?: string;
  latitude?: string;
  longitude?: string;
}

interface ZippopotamResponse {
  places?: ZippopotamPlace[];
}

async function geocodeZip(zipCode: string): Promise<GeoEntry> {
  const cached = geoCache.get(zipCode);
  if (cached && Date.now() - cached.fetchedAt < GEO_TTL_MS) return cached;

  const res = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zipCode)}`);
  if (!res.ok) {
    throw new Error(`Could not geocode ZIP ${zipCode} (HTTP ${res.status})`);
  }
  const body = (await res.json()) as ZippopotamResponse;
  const place = body.places?.[0];
  if (!place || !place.latitude || !place.longitude) {
    throw new Error(`No place data for ZIP ${zipCode}`);
  }
  const lat = Number(place.latitude);
  const lng = Number(place.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error(`Invalid coordinates for ZIP ${zipCode}`);
  }
  const cityName = place['place name'] ?? '';
  const stateAbbr = place['state abbreviation'] ?? place.state ?? '';
  const entry: GeoEntry = {
    latitude: lat,
    longitude: lng,
    location: stateAbbr ? `${cityName}, ${stateAbbr}` : cityName || zipCode,
    fetchedAt: Date.now(),
  };
  geoCache.set(zipCode, entry);
  return entry;
}

interface OpenMeteoResponse {
  current_weather?: {
    temperature?: number;
    weathercode?: number;
  };
  daily?: {
    time?: string[];
    temperature_2m_max?: number[];
    temperature_2m_min?: number[];
    weathercode?: number[];
  };
}

async function fetchForecast(zipCode: string): Promise<WeatherSnapshot> {
  const geo = await geocodeZip(zipCode);
  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', geo.latitude.toFixed(4));
  url.searchParams.set('longitude', geo.longitude.toFixed(4));
  url.searchParams.set('current_weather', 'true');
  url.searchParams.set('temperature_unit', 'fahrenheit');
  url.searchParams.set('windspeed_unit', 'mph');
  url.searchParams.set('precipitation_unit', 'inch');
  url.searchParams.set('timezone', 'auto');
  url.searchParams.set('forecast_days', '5');
  url.searchParams.set(
    'daily',
    'temperature_2m_max,temperature_2m_min,weathercode',
  );

  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`Weather feed returned HTTP ${res.status}`);
  }
  const body = (await res.json()) as OpenMeteoResponse;
  const daily = body.daily ?? {};
  const times = daily.time ?? [];
  const highs = daily.temperature_2m_max ?? [];
  const lows = daily.temperature_2m_min ?? [];
  const codes = daily.weathercode ?? [];
  const days: WeatherDay[] = [];
  for (let i = 0; i < times.length && i < 5; i += 1) {
    days.push({
      date: times[i],
      highF: Math.round(highs[i] ?? 0),
      lowF: Math.round(lows[i] ?? 0),
      weatherCode: Math.round(codes[i] ?? 0),
    });
  }
  if (days.length === 0) {
    throw new Error('Weather feed returned no daily forecast');
  }
  return {
    zipCode,
    location: geo.location,
    currentF: Math.round(body.current_weather?.temperature ?? days[0].highF),
    currentWeatherCode: Math.round(body.current_weather?.weathercode ?? days[0].weatherCode),
    days,
    fetchedAt: Date.now(),
  };
}

export async function getWeatherSnapshot(zipCode: string): Promise<WeatherSnapshot> {
  const cached = forecastCache.get(zipCode);
  if (cached && Date.now() - cached.fetchedAt < FORECAST_TTL_MS) return cached;

  const existing = inflight.get(zipCode);
  if (existing) return existing;

  const p = fetchForecast(zipCode)
    .then((snap) => {
      forecastCache.set(zipCode, snap);
      return snap;
    })
    .finally(() => {
      inflight.delete(zipCode);
    });
  inflight.set(zipCode, p);
  return p;
}

// =============================================================================
// WMO weather codes -> human label + icon hint. Codes per
// https://open-meteo.com/en/docs#weathervariables.
// =============================================================================

export type WeatherIconKind =
  | 'sun'
  | 'cloud-sun'
  | 'cloud'
  | 'cloud-fog'
  | 'cloud-drizzle'
  | 'cloud-rain'
  | 'cloud-snow'
  | 'cloud-lightning';

export function describeWeatherCode(code: number): { label: string; icon: WeatherIconKind } {
  if (code === 0) return { label: 'Clear', icon: 'sun' };
  if (code === 1) return { label: 'Mostly Sunny', icon: 'cloud-sun' };
  if (code === 2) return { label: 'Partly Cloudy', icon: 'cloud-sun' };
  if (code === 3) return { label: 'Overcast', icon: 'cloud' };
  if (code === 45 || code === 48) return { label: 'Fog', icon: 'cloud-fog' };
  if (code >= 51 && code <= 57) return { label: 'Drizzle', icon: 'cloud-drizzle' };
  if (code >= 61 && code <= 67) return { label: 'Rain', icon: 'cloud-rain' };
  if (code >= 80 && code <= 82) return { label: 'Showers', icon: 'cloud-rain' };
  if (code >= 71 && code <= 77) return { label: 'Snow', icon: 'cloud-snow' };
  if (code === 85 || code === 86) return { label: 'Snow Showers', icon: 'cloud-snow' };
  if (code >= 95 && code <= 99) return { label: 'Thunderstorm', icon: 'cloud-lightning' };
  return { label: 'Cloudy', icon: 'cloud' };
}
